import React from 'react';

import {
  reservationStore,
  Step1Summary,
  Step2Summary,
  Step3Summary,
  StepSummary,
} from '../../state/reservation-store';
import { displayDate } from '@camp67/model/booking/dates';
import { useMessages, FormattedString } from '../../i18n/useMessages';
import { MessageKey } from '../../locales/en';
import Sprite from '../../sprites/sprite';
import clsx from 'clsx';
import { Tooltip } from '../tooltip';
import { useAnalytics } from '../../hooks/use-analytics';
import { AmplitudeEvent, ButtonSource, ButtonTag } from '../../state/amplitude';

type StepNumber = 1 | 2 | 3;
export type Step = `step-${StepNumber}`;

interface BookingFormSectionProps {
  children: React.ReactNode;
  showExpanded?: boolean;
  isStepDisabled?: boolean;
  step: Step;
  messageKey: MessageKey;
  onExpand: VoidFunction;
  onNext: (step: Step) => void;
}

const getStepSummary = (step: Step) => {
  switch (step) {
    case 'step-1':
      return reservationStore.getStep1Summary();
    case 'step-2':
      return reservationStore.getStep2Summary();
    case 'step-3':
      return reservationStore.getStep3Summary();
  }
};

const getStepIsComplete = (step: Step) => {
  switch (step) {
    case 'step-1':
      return reservationStore.isStep1Complete();
    case 'step-2':
      return reservationStore.isStep2Complete();
    case 'step-3':
      return reservationStore.isStep3Complete();
  }
};

const getStepResetCallback = (step: Step) => {
  switch (step) {
    case 'step-1':
      return reservationStore.resetStep1;
    case 'step-2':
      return reservationStore.resetStep2;
    case 'step-3':
      return reservationStore.resetStep3;
  }
};

const getStepDisabledTooltip = (step: Step): MessageKey => {
  switch (step) {
    case 'step-1':
      return 'camp67-reservation.form.step-1.next.tooltip.disabled';
    case 'step-2':
      return 'camp67-reservation.form.step-2.next.tooltip.disabled';
    case 'step-3':
      return 'camp67-reservation.form.step-3.next.tooltip.disabled';
  }
};

export const BookingFormSectionV3: React.FC<BookingFormSectionProps> = ({
  children,
  showExpanded = false,
  isStepDisabled = true,
  step,
  messageKey,
  onExpand,
  onNext,
}) => {
  const getMessage = useMessages();
  const summary = getStepSummary(step);
  const isComplete = getStepIsComplete(step);

  const onReset = getStepResetCallback(step);
  const amplitude = useAnalytics();

  const currentStep = parseInt(step.split('-')[1]) as StepNumber;

  const primaryActionMessage =
    currentStep !== 3
      ? getMessage(`camp67-reservation.form.step.next`)
      : getMessage('camp67.continue');

  const onClick = () => {
    amplitude?.track(AmplitudeEvent.BUTTON_CLICK, {
      buttonSource: ButtonSource[`STEP_${parseInt(step.split('-')[1]) as 1 | 2 | 3}`],
      buttonTag: ButtonTag.NEXT_STEP,
    });
    onNext(step);
  };

  const tooltipMessage = getMessage(getStepDisabledTooltip(step));

  return (
    <Tooltip
      text={isStepDisabled && getMessage('camp67-reservation.form.step.disabled.tooltip')}
    >
      <div
        className={clsx('relative w-full transition', {
          'opacity-50': isStepDisabled,
          'opacity-100': !isStepDisabled,
        })}
      >
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
        <legend
          onClick={onExpand}
          className={clsx(
            'hidden transition-all text-left sm:flex flex-row items-start sm:items-center gap-1 justify-between w-full',
            {
              'cursor-pointer': !isStepDisabled,
              'cursor-normal': isStepDisabled,
            },
          )}
        >
          <span className="flex font-serif flex-row flex-wrap items-center gap-2 uppercase text-xs sm:text-sm tracking-wider">
            <span>
              <FormattedString message={messageKey} />
            </span>
            {isComplete ? (
              <Sprite name="tick" width="25" height="25" className="w-6 h-6" />
            ) : null}
          </span>
          <div className="border-dotted flex-1 w-full border-b-2 mx-2 border-gray-300"></div>
          <Tooltip
            text={
              !showExpanded
                ? getMessage('camp67-reservation.form.step.expand')
                : getMessage('camp67-reservation.form.step.collapse')
            }
          >
            <Sprite
              name="down"
              width="56"
              height="30"
              className={clsx('min-w-4 min-h-4 w-4 h-4 transition sm:pt-0', {
                'rotate-180 mt-1': showExpanded,
                'mt-1': !showExpanded,
              })}
              stroke="black"
              strokeWidth={8}
            />
          </Tooltip>
        </legend>
        {showExpanded ? (
          <div className="bg-white w-full sm:mt-3 rounded-lg">
            {children}
            <div className="mt-4 w-full flex flex-row items-center justify-end gap-3 px-6 pb-6">
              <button
                type="button"
                onClick={onReset}
                className="px-3 py-2 rounded-md bg-white"
              >
                {getMessage('camp67.reset')}
              </button>
              <Tooltip text={!isComplete && tooltipMessage}>
                <button
                  disabled={!isComplete}
                  type="button"
                  className={clsx('px-3 py-2 rounded-md bg-green-200', {
                    'opacity-50': !isComplete,
                    'opacity-100': isComplete,
                  })}
                  onClick={onClick}
                >
                  {primaryActionMessage}
                </button>
              </Tooltip>
            </div>
          </div>
        ) : !isStepDisabled ? (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus
          <div onClick={onExpand} className="w-full mt-2 mb-3" role="button">
            <StepSummarised summary={summary} />
          </div>
        ) : null}
      </div>
    </Tooltip>
  );
};

export const BookingFormSectionV3Mobile: React.FC<BookingFormSectionProps> = ({
  children,
  showExpanded = false,
  isStepDisabled = true,
  step,
  messageKey,
  onExpand,
}) => {
  const getMessage = useMessages();
  const summary = getStepSummary(step);
  const isComplete = getStepIsComplete(step);

  return (
    <Tooltip
      text={isStepDisabled && getMessage('camp67-reservation.form.step.disabled.tooltip')}
    >
      <div
        className={clsx('relative w-full transition bg-yellow-50', {
          'opacity-50': isStepDisabled,
          'border-b-2 border-dotted': isStepDisabled,
          'border-t-2 border-dotted': step === 'step-1',
          'opacity-100': !isStepDisabled,
        })}
      >
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
        <legend
          onClick={onExpand}
          className={clsx(
            'transition-all text-left flex flex-row items-center gap-1 justify-between w-full px-4 py-4',
            {
              'cursor-pointer': !isStepDisabled,
              'cursor-normal': isStepDisabled,
            },
          )}
        >
          <span className="flex font-serif flex-row flex-wrap items-center gap-2 uppercase text-xs sm:text-sm tracking-wider">
            <span>
              <FormattedString message={messageKey} />
            </span>
            {isComplete ? (
              <Sprite name="tick" width="25" height="25" className="w-6 h-6" />
            ) : null}
          </span>
          <Sprite
            name="down"
            width="56"
            height="30"
            className={clsx('min-w-4 min-h-4 w-4 h-4 transition', {
              'rotate-180 mt-1': showExpanded,
              'mt-1': !showExpanded,
            })}
            stroke="black"
            strokeWidth={8}
          />
        </legend>
        {showExpanded ? (
          <div className="bg-white w-full rounded-lg pb-6">{children}</div>
        ) : !isStepDisabled ? (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus
          <div onClick={onExpand} className="w-full" role="button">
            <StepSummarised summary={summary} />
          </div>
        ) : null}
      </div>
    </Tooltip>
  );
};

const StepSummarised = ({ summary }: { summary: StepSummary }) => {
  switch (summary.step) {
    case 1:
      return <Step1Summarised {...summary} />;
    case 2:
      return <Step2Summarised {...summary} />;
    case 3:
      return <Step3Summarised {...summary} />;
  }
};

const EmptyText = ({ text }: { text: string }) => {
  return <span className="text-[#666]">{text}</span>;
};

const Step1Summarised = ({ rooms }: Step1Summary) => {
  const getMessage = useMessages();
  const isRoomsEmpty = !rooms;

  return (
    <div className="flex flex-col gap-1 bg-white px-6 py-6 rounded-lg overflow-hidden">
      <p className="text-gray-500 text-sm">
        {getMessage('camp67-reservation.form.step-1.summary.rooms')}
      </p>
      <p>
        {isRoomsEmpty ? (
          <EmptyText
            text={getMessage('camp67-reservation.form.step-1.summary.rooms.empty')}
          />
        ) : (
          rooms
        )}
      </p>
    </div>
  );
};

const Step2Summarised = ({ startDate, endDate, guests }: Step2Summary) => {
  const getMessage = useMessages();

  const isStartDateEmpty = !startDate;
  const isEndDateEmpty = !endDate;
  const displayStartDate = startDate && displayDate(startDate).split('2025')[0].trim();
  const datesMessage = isStartDateEmpty ? (
    <EmptyText
      text={getMessage('camp67-reservation.form.step-2.summary.dates.empty-start')}
    />
  ) : isEndDateEmpty ? (
    <EmptyText
      text={getMessage('camp67-reservation.form.step-2.summary.dates.empty-end')}
    />
  ) : (
    <p>
      {displayStartDate}{' '}
      {getMessage('camp67-reservation.form.step-2.summary.dates.value')}{' '}
      {displayDate(endDate)}
    </p>
  );

  const isGuestsEmpty = !guests;
  const guestsMessage = isGuestsEmpty ? (
    <EmptyText text={getMessage('camp67-reservation.form.step-2.summary.guests.empty')} />
  ) : (
    guests
  );

  return (
    <div className="flex flex-col gap-4 sm:gap-8 sm:flex-row items-start px-6 py-6 bg-white rounded-lg overflow-hidden">
      <div className="flex flex-col gap-1">
        <p className="text-gray-500 text-sm">
          {getMessage('camp67-reservation.form.step-2.summary.dates')}
        </p>
        <>{datesMessage}</>
      </div>
      <div className="flex flex-col gap-1">
        <p className="text-gray-500 text-sm">
          {getMessage('camp67-reservation.form.step-2.summary.guests')}
        </p>
        <p>{guestsMessage}</p>
      </div>
    </div>
  );
};

const Step3Summarised = ({ guestDetails, primary }: Step3Summary) => {
  const isGuestDetailsEmpty = !guestDetails.some((g) => g);
  const isPrimaryContactEmpty = !primary.some((p) => p);

  const getMessage = useMessages();

  return (
    <div className="flex flex-row flex-wrap gap-4 items-start sm:gap-8 px-6 py-6 bg-white rounded-lg overflow-hidden">
      <div className="flex flex-col gap-1">
        <p className="text-gray-500 text-sm">
          {getMessage('camp67-reservation.form.step-3.summary.guests')}
        </p>
        {isGuestDetailsEmpty ? (
          <EmptyText
            text={getMessage('camp67-reservation.form.step-3.summary.guests.empty')}
          />
        ) : (
          guestDetails.map((d) => <p key={d}>{d}</p>)
        )}
      </div>
      <div className="flex flex-col gap-1">
        <p className="text-gray-500 text-sm">
          {getMessage('camp67-reservation.form.step-3.summary.primary')}
        </p>
        {isPrimaryContactEmpty ? (
          <EmptyText
            text={getMessage('camp67-reservation.form.step-3.summary.primary.empty')}
          />
        ) : (
          primary.map((d) => <p key={d}>{d}</p>)
        )}
      </div>
    </div>
  );
};
